import { set } from 'radash';
import {
  lazy,
  createSignal,
  createEffect,
  createResource,
  on,
  onCleanup,
} from 'solid-js';
import { Dynamic, template } from 'solid-js/web';

//const icons = import.meta.glob('./icons/*/*.svg', { as: 'component-solid' });
//console.log('Loading icons', icons);

const ICON_PATH = '/icons';

const emptyIcon = (props) => {
  //Empty icon with size props.size
  return <svg width={props.size} height={props.size}></svg>;
};

// EID-99: the empty-icon placeholder used to call URL.createObjectURL on
// every invocation and never revoke it, leaking one blob per Icon mount or
// prop update. Cache the URL per size and ref-count it so identical requests
// share a single URL, revoked only once nothing holds it any more. Callers
// that never release (e.g. a worldlayer stashing the URL once at init) simply
// keep their share alive forever — no worse than the previous never-revoked
// behaviour, just no longer duplicated per call.
const emptyIconCache = new Map(); // size -> { url, refCount }

const acquireEmptyIconUrl = (size) => {
  const key = String(size);
  let entry = emptyIconCache.get(key);
  if (!entry) {
    const url = window.URL.createObjectURL(
      new Blob([emptyIcon({ size })], { type: 'image/svg+xml' }),
    );
    entry = { url, refCount: 0 };
    emptyIconCache.set(key, entry);
  }
  entry.refCount += 1;
  return entry.url;
};

const releaseEmptyIconUrl = (size) => {
  const key = String(size);
  const entry = emptyIconCache.get(key);
  if (!entry) return;
  entry.refCount -= 1;
  if (entry.refCount <= 0) {
    window.URL.revokeObjectURL(entry.url);
    emptyIconCache.delete(key);
  }
};

export const loadIcon = async ({ url, size, color }) => {
  let iconurl;
  if (!url) return acquireEmptyIconUrl(size);
  try {
    iconurl = new URL(url);
  } catch (e) {
    console.log('Badly formed icon URL', url);
    return null;
  }
  if (iconurl.protocol != 'icon:') return url;
  let name = iconurl.hostname.replace(/\//g, '');
  const weight = iconurl.searchParams.get('weight') || 'regular';
  const icolor = iconurl.searchParams.get('color') || color || 'currentColor';
  if (weight != 'regular') name = name + '-' + weight;
  try {
    const icon = await fetch(`${ICON_PATH}/${weight}/${name}.svg`);
    let svg = await icon.text();
    svg = svg.replace(/currentColor/g, icolor);
    if (size)
      svg = svg.replace(/<svg/g, `<svg width="${size}" height="${size}"`);
    return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
  } catch {
    console.log('Icon not found', name);
    return null;
  }
};

export const Icon = (props) => {
  const [icon, setIcon] = createSignal(null);
  createEffect(() => {
    const wasEmpty = !props.url;
    const size = props.size;
    // Release this run's empty-icon hold (if any) either when the effect
    // re-runs for new props (replacement) or the component unmounts.
    onCleanup(() => {
      if (wasEmpty) releaseEmptyIconUrl(size);
    });
    loadIcon(props).then(setIcon);
  });
  return <img src={icon()} />;
};
